import { addOnFunctionNetworkError, addOnFunctionNetworkSuccess } from "common/call-cloud-function";
import { initializeLocalization } from "common/components/localization";
// import { registerComponentHandlers } from "generator/components/register-all";
import { isCustomDomain } from "common/customDomains";
import { logError, logInfo, setShouldLog } from "common/debug-print";
import { getFeatureFlag } from "common/feature-flags";
import { setFirestoreIDGeneratorRNG } from "common/id-generator";
import { getStaticAppConfig } from "common/static-app";
import { registerFrontendEventSender } from "common/tracing";
import "focus-visible";
import { browserIsSafari } from "lib/browser-detect";
import { ErrorBoundary } from "lib/error-boundary";
import { initFirebase } from "lib/firebase";
import { honeycombSendEvent } from "lib/honeycomb";
import { hookIndexedDBForUnreliableOpens } from "lib/indexeddb-hacks";
import { lazilyLoadedComponent } from "lib/react-lazy-loading";
import { Suspense } from "react";
import ReactDOM from "react-dom";
import type { RouteComponentProps } from "react-router-dom";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import AppStateProvider from "sharedUI/app-state-context";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import { GlobalStyles } from "twin.macro";
import { initRefreshOnStandalone, standalone } from "webapp/lib/device";
import { initializeAnalytics } from "./analytics";
import { monitorSafariStateHack } from "./lib/browser-hacks";
import { reloadBrowserWindow } from "./lib/browser-reload";
import { cryptoRandom } from "./lib/crypto-random";
import { forceAppID } from "./lib/force-id";
import { initFeatureFlagsWithStorage } from "./lib/init-feature-flags";
import { registerLongPress } from "./lib/long-press-event";
import { setOriginOnline, trapFirestoreHttpErrors } from "./lib/network-tracking";
import { disableBounce, disableContext } from "./lib/nobounce";
import { default as registerServiceWorker } from "./lib/register-service-worker";
import { watchUpdates } from "./lib/watch-updates";
import * as routes from "./routes";
import { getBuilderTheme } from "./sharedUI/styles";
import GlideAppPlayer from "./webapp/glide-app-player";

const App = lazilyLoadedComponent("designer", () => import("./designer/designer"));

async function preparePushNotifications(): Promise<void> {
    const { prepareActivePushNotifications } = await import("./webapp/lib/push-notifications");
    await prepareActivePushNotifications();
}

function GlideAppPlayerRoute(p: RouteComponentProps<{ id: string }>) {
    const appID = decodeURIComponent(p.match.params.id);
    return <GlideAppPlayer id={appID} />;
}

function Designer() {
    return (
        <Suspense fallback={<div />}>
            <App />
        </Suspense>
    );
}

function render() {
    let rootRoutes: React.ReactElement<any>;

    if (isCustomDomain(window.location.hostname) || forceAppID !== undefined || getStaticAppConfig() !== undefined) {
        rootRoutes = (
            <Switch>
                <Route exact={false} path={routes.customDomainPlayPattern} component={GlideAppPlayer} />
            </Switch>
        );
    } else {
        rootRoutes = (
            <ErrorBoundary>
                <Switch>
                    <Route exact={false} path={routes.playPattern} component={GlideAppPlayerRoute} />
                    <Route component={Designer} />
                </Switch>
            </ErrorBoundary>
        );
    }

    ReactDOM.render(
        <Router>
            <GlobalStyles />
            <AppStateProvider>
                <TailwindThemeProvider theme={getBuilderTheme(false)}>{rootRoutes}</TailwindThemeProvider>
            </AppStateProvider>
        </Router>,
        document.getElementById("root") as HTMLElement
    );
}

function mainWithLocalizationLoaded() {
    initFirebase();
    monitorSafariStateHack();
    // This improves the entropy of Firestore ID generation.
    // Math.random isn't guaranteed to be a particularly good source
    // of random values, so we sort of reimplement it using
    // window.crypto.getRandomValues if it is available.
    setFirestoreIDGeneratorRNG(cryptoRandom);
    registerFrontendEventSender(honeycombSendEvent, false);
    trapFirestoreHttpErrors();

    initFeatureFlagsWithStorage();
    registerServiceWorker();
    initializeAnalytics().catch(e => {
        logError(`Could not initialize analytics: ${e}`);
    });
    setShouldLog(() => getFeatureFlag("debugPrint"));

    // Firestore will enable persistence only if the window is standalone when it is first loaded.
    // If the window becomes standalone (which would enable persistence), then we must refresh so that Firestore has a
    // chance to enable its persistence.
    initRefreshOnStandalone();
    if (standalone) {
        // NOTE: Any new lazy-loaded components must be added here to work in offline mode!
        // If you don't, the user will need to lazy-load them online first, then go offline.
        const preInstall = async () => {
            const FormSubmissionSuccessToast = import(
                "webapp/chrome/views/form-submission-success-toast/form-submission-success-toast"
            );
            const QRCode = import("qrcode.react");
            const BarChart = import("webapp/ui/smart/charts/bar-chart/bar-chart");
            const YouTubePlayer = import("webapp/ui/deprecated/youtube-player");
            const ComponentContextMenu = import("sharedUI/ui/smart/component-context-menu/component-context-menu");
            const MarkdownView = import("webapp/ui/views/markdown/markdown-view");
            const PieChart = import("webapp/ui/smart/charts/pie-chart/pie-chart");
            const StackedBarChart = import("webapp/ui/smart/charts/stacked-bar-chart/stacked-bar-chart");
            const VideoPlayer = import("webapp/ui/smart/video-player/video-player");
            const AS = import("react-virtualized/dist/es/AutoSizer");
            const MaterialDateTimePicker = import("webapp/ui/smart/date-time-picker/private/material-date-time-picker");
            const ImageOverlayView = import("webapp/ui/views/image-overlay/image-overlay-view");
            const MapBoxMap = import("webapp/ui/smart/map-list/private/mapbox-map-list-map");
            await Promise.all([
                FormSubmissionSuccessToast,
                QRCode,
                BarChart,
                YouTubePlayer,
                ComponentContextMenu,
                MarkdownView,
                PieChart,
                StackedBarChart,
                VideoPlayer,
                AS,
                MaterialDateTimePicker,
                ImageOverlayView,
                MapBoxMap,
            ]);
        };

        setTimeout(() => {
            preInstall().catch(err => logInfo("Failed to install modules", err));
        }, 3500);
    }

    if (forceAppID === undefined) {
        watchUpdates().catch(err => logInfo(err));
    }
    disableContext();
    disableBounce();

    registerLongPress(window, document);
    preparePushNotifications().catch(e => logInfo("Could not prepare push notifications", e));

    const onConnected = () => setOriginOnline(true);
    const onDisconnected = () => setOriginOnline(false);
    addOnFunctionNetworkError(onDisconnected);
    addOnFunctionNetworkSuccess(onConnected);
    // We used to use navigator.onLine, window.ononline, and window.onoffline
    // to detect network connectivity. We can no longer do this due to either
    // a bug in macOS Big Sur, a bug in Chromium, or a bug in both. See
    // https://bugs.chromium.org/p/chromium/issues/detail?id=1236624.
    //
    // This practice was mostly unnecessary. Firestore Listener errors give us
    // a slightly delayed "offline" signal compared to the "offline" event,
    // under the assumption that the "offline" event is not firing false
    // positives. Since "offline" is now known to fire false positives without
    // ever recovering with a true negative, we have to abandon it and live
    // with the slight delay incurred by the Firestore Listener error handlers.
    //
    // We're going to have to do something similar when we finally migrate
    // off Firestore.

    render();
}

function main() {
    (window as any).glidebeacon = true;
    const r = document.getElementById("reload-area");
    if (r !== null) {
        r.style.display = "none";
    }

    let { language } = window.navigator;

    // This has to be in here, and not in `initializeLocalization`, because
    // the latter is in `common` and can't use `browser-detect`.
    // See https://github.com/quicktype/glide/issues/6157
    if (browserIsSafari) {
        if (language === "pt") {
            language = "pt-BR";
        } else if (language === "en") {
            language = "en-US";
        }
    }

    // See https://bugs.webkit.org/show_bug.cgi?id=226547 for the motivation behind
    // this. It is possible on certain versions of Safari for the initial connection
    // request to hang indefinitely. However, a valid workaround is to make multiple
    // connection requests: the later ones succeed where the eariler ones fail.

    // This was not a part of anyone's failure expectations for IndexedDB, so we have
    // to monkey-patch it, particularly for Firebase.
    hookIndexedDBForUnreliableOpens(window);

    // Mind the .catch().then().catch() chain.
    // If we reject while trying to load localization, we reload.
    // If mainWithLocalizationLoaded() crashes then we just log it.
    // FIXME: Is that the most appropriate thing to do?

    initializeLocalization(language)
        .catch(() => reloadBrowserWindow("localization initialization failed"))
        .then(() => mainWithLocalizationLoaded())
        .catch(e => logError(e));
}

main();
